import { generateTraceparent } from "../core/identity"
import type { SessionEvent } from "../core/types"

export type PushFn = (event: SessionEvent, isError?: boolean) => void

/** Same rule as web: RELIA's own telemetry must never be recorded as user traffic. Duplicated here so the native bundle never imports browser code. */
export function isNativeTelemetryUrl(urlStr: string): boolean {
  const u = (urlStr || "").toLowerCase()
  if (!u) return false
  if (u.includes("/sessions/ingest") || u.includes("/sessions/replay") || u.includes("/sessions/finalize")) return true
  if (u.includes("/api/v1/sessions")) return true
  return false
}

function endpointHost(endpoint: string): string {
  try {
    return new URL(endpoint).hostname.toLowerCase()
  } catch {
    return ""
  }
}

/**
 * Patch global fetch + XHR (both exist in React Native) to record network events.
 * traceparent is injected ONLY for requests to the RELIA backend host, never 3rd parties.
 * Returns a restore function. Safe no-op when fetch is undefined.
 */
export function patchGlobalNetwork(
  push: PushFn,
  endpoint: string,
  clock: () => number = () => Date.now(),
): () => void {
  const g = globalThis as any
  const host = endpointHost(endpoint)
  const restores: Array<() => void> = []

  if (typeof g.fetch === "function") {
    const originalFetch = g.fetch
    const patched = async function (input: any, init?: any): Promise<any> {
      const urlStr =
        typeof input === "string" ? input : input instanceof URL ? input.toString() : input?.url || ""
      if (!urlStr || isNativeTelemetryUrl(urlStr)) return originalFetch.call(g, input, init)
      const toHost = Boolean(host) && urlStr.toLowerCase().includes(host)
      const { header, traceId } = generateTraceparent()
      let fetchInput = input
      let fetchInit = init
      if (toHost) {
        const headers: Record<string, string> = { ...(init?.headers || {}) }
        if (!headers.traceparent && !headers.Traceparent) headers.traceparent = header
        fetchInit = { ...(init || {}), headers }
      }
      const method = String(init?.method || input?.method || "GET").toUpperCase()
      const start = clock()
      try {
        const response = await originalFetch.call(g, fetchInput, fetchInit)
        const duration = Math.max(0, clock() - start)
        const status = Number(response?.status ?? 0)
        push(
          {
            type: "network",
            ts: new Date().toISOString(),
            target: "",
            path: "",
            text: `${method} ${String(urlStr).split("?")[0]} - ${status}`,
            meta: {
              url: String(urlStr).slice(0, 500),
              method,
              status: String(status),
              duration_ms: duration,
              trace_id: toHost ? traceId : "",
            },
          },
          status >= 400,
        )
        return response
      } catch (err: any) {
        const duration = Math.max(0, clock() - start)
        push(
          {
            type: "network_error",
            ts: new Date().toISOString(),
            target: "",
            path: "",
            text: `${method} ${String(urlStr).split("?")[0]} FAILED`,
            meta: {
              url: String(urlStr).slice(0, 500),
              method,
              error: String(err?.message || err).slice(0, 300),
              duration_ms: duration,
              trace_id: toHost ? traceId : "",
            },
          },
          true,
        )
        throw err
      }
    }
    g.fetch = patched
    restores.push(() => {
      g.fetch = originalFetch
    })
  }

  const XHR = g.XMLHttpRequest
  if (typeof XHR === "function" && XHR.prototype && typeof XHR.prototype.open === "function") {
    const XHROpen = XHR.prototype.open
    const XHRSend = XHR.prototype.send
    XHR.prototype.open = function (method: string, url: string, ...rest: unknown[]) {
      const xhr = this as any
      xhr._relia_method = String(method || "GET").toUpperCase()
      xhr._relia_url = typeof url === "string" ? url : String(url)
      xhr._relia_start = clock()
      return (XHROpen as any).call(this, method, url, ...(rest as []))
    }
    XHR.prototype.send = function (body?: any) {
      const xhr = this as any
      this.addEventListener("loadend", function (this: any) {
        const urlStr = String(xhr._relia_url || "")
        if (!urlStr || isNativeTelemetryUrl(urlStr)) return
        if (this.status === 0) return
        const method = xhr._relia_method || "GET"
        const duration = Math.max(0, clock() - (xhr._relia_start || clock()))
        const isError = Number(this.status) >= 400
        push(
          {
            type: isError ? "network_error" : "network",
            ts: new Date().toISOString(),
            target: "",
            path: "",
            text: `${method} ${urlStr.split("?")[0]} - ${this.status}`,
            meta: {
              url: urlStr.slice(0, 500),
              method,
              status: String(this.status),
              duration_ms: duration,
              trace_id: "",
            },
          },
          isError,
        )
      })
      return XHRSend.call(this, body)
    }
    restores.push(() => {
      XHR.prototype.open = XHROpen
      XHR.prototype.send = XHRSend
    })
  }

  return () => {
    for (const r of restores) r()
  }
}
